import React, { useState, useEffect } from "react";
import { FaTrash, FaEdit, FaSave, FaTimes, FaUsers, FaCog, FaCloud } from "react-icons/fa";
import Swal from "sweetalert2";
import {
    Button,
    TextField,
    Typography,
    Box,
    Grid,
    Autocomplete,
    Chip,
    Tabs,
    Tab,
} from "@mui/material";
import UserManagement from "./UserManagement";

const AWS_REGIONS = [
    { code: 'us-east-1', name: 'US East (N. Virginia)' },
    { code: 'us-east-2', name: 'US East (Ohio)' },
    { code: 'us-west-1', name: 'US West (N. California)' },
    { code: 'us-west-2', name: 'US West (Oregon)' },
    { code: 'ca-central-1', name: 'Canada (Central)' },
    { code: 'eu-north-1', name: 'Europe (Stockholm)' },
    { code: 'eu-west-1', name: 'Europe (Ireland)' },
    { code: 'eu-west-2', name: 'Europe (London)' },
    { code: 'eu-west-3', name: 'Europe (Paris)' },
    { code: 'eu-central-1', name: 'Europe (Frankfurt)' },
    { code: 'eu-central-2', name: 'Europe (Zurich)' },
    { code: 'eu-south-1', name: 'Europe (Milan)' },
    { code: 'eu-south-2', name: 'Europe (Spain)' },
    { code: 'ap-south-1', name: 'Asia Pacific (Mumbai)' },
    { code: 'ap-south-2', name: 'Asia Pacific (Hyderabad)' },
    { code: 'ap-northeast-1', name: 'Asia Pacific (Tokyo)' },
    { code: 'ap-northeast-2', name: 'Asia Pacific (Seoul)' },
    { code: 'ap-northeast-3', name: 'Asia Pacific (Osaka)' },
    { code: 'ap-southeast-1', name: 'Asia Pacific (Singapore)' },
    { code: 'ap-southeast-2', name: 'Asia Pacific (Sydney)' },
    { code: 'ap-southeast-3', name: 'Asia Pacific (Jakarta)' },
    { code: 'ap-southeast-4', name: 'Asia Pacific (Melbourne)' },
    { code: 'ap-east-1', name: 'Asia Pacific (Hong Kong)' },
    { code: 'sa-east-1', name: 'South America (São Paulo)' },
    { code: 'me-south-1', name: 'Middle East (Bahrain)' },
    { code: 'me-central-1', name: 'Middle East (UAE)' },
    { code: 'af-south-1', name: 'Africa (Cape Town)' },
    { code: 'il-central-1', name: 'Israel (Tel Aviv)' },
];

const DEFAULT_REGIONS = ['us-west-2', 'us-east-1', 'ap-southeast-2'];
const V2_API_BASE = 'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/tenant/settings';

const TenantSettingsPage = ({ isSidebarOpen }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [tenantName, setTenantName] = useState("My Tenant");
    const [isEditingTenantName, setIsEditingTenantName] = useState(false);
    const [scanRegions, setScanRegions] = useState([]);
    const [loadingRegions, setLoadingRegions] = useState(true);
    const [savingRegions, setSavingRegions] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const tenantID = localStorage.getItem("tenantID") || localStorage.getItem("organizationID");
                if (!tenantID) return;

                const response = await fetch(`${V2_API_BASE}?tenantID=${tenantID}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.tenantName) setTenantName(data.tenantName);
                    const regions = data.scanRegions ? data.scanRegions.split(',') : DEFAULT_REGIONS;
                    setScanRegions(regions);
                }
            } catch (error) {
                console.error("Error fetching settings:", error);
                setScanRegions(DEFAULT_REGIONS);
            } finally {
                setLoadingRegions(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSaveRegions = async () => {
        if (scanRegions.length === 0) {
            Swal.fire({
                title: "Error",
                text: "Please select at least one cloud region.",
                icon: "error",
            });
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Update scan regions to: ${scanRegions.join(', ')}?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#342ead",
        });

        if (result.isConfirmed) {
            setSavingRegions(true);
            try {
                const tenantID = localStorage.getItem("tenantID") || localStorage.getItem("organizationID");
                const response = await fetch(V2_API_BASE, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ tenantID, scanRegions: scanRegions.join(',') }),
                });

                if (response.ok) {
                    Swal.fire({ title: "Updated!", text: "Scan regions updated successfully.", icon: "success" });
                } else {
                    Swal.fire({ title: "Error", text: "Failed to update scan regions.", icon: "error" });
                }
            } catch (error) {
                Swal.fire({ title: "Error", text: error.message, icon: "error" });
            } finally {
                setSavingRegions(false);
            }
        }
    };

    const handleSaveTenantName = async () => {
        if (!tenantName || tenantName.trim() === "") {
            Swal.fire({ title: "Error", text: "Tenant name cannot be empty.", icon: "error" });
            return;
        }

        try {
            const tenantID = localStorage.getItem("tenantID") || localStorage.getItem("organizationID");
            const response = await fetch(V2_API_BASE, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tenantID, tenantName }),
            });

            if (response.ok) {
                setIsEditingTenantName(false);
                Swal.fire({ title: "Updated!", text: `Tenant name updated to: "${tenantName}"`, icon: "success" });
            } else {
                Swal.fire({ title: "Error", text: "Failed to update tenant name.", icon: "error" });
            }
        } catch (error) {
            Swal.fire({ title: "Error", text: error.message, icon: "error" });
        }
    };

    const handleCancelTenantName = async () => {
        try {
            const tenantID = localStorage.getItem("tenantID") || localStorage.getItem("organizationID");
            const response = await fetch(`${V2_API_BASE}?tenantID=${tenantID}`);
            if (response.ok) {
                const data = await response.json();
                setTenantName(data.tenantName || "My Tenant");
            }
        } catch (error) {
            setTenantName("My Tenant");
        }
        setIsEditingTenantName(false);
    };

    return (
        <Box sx={{ transition: "margin-left 0.3s ease", marginLeft: isSidebarOpen ? "200px" : "0px", marginRight: isSidebarOpen ? "200px" : "0px", padding: "20px", paddingTop: "100px", minHeight: "100vh" }}>
            <Typography variant="h4" gutterBottom sx={{ textAlign: "start", marginBottom: "30px", fontWeight: "bold" }}>
                Tenant Settings
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '30px' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab icon={<FaCog />} label="General" />
                    <Tab icon={<FaCloud />} label="Cloud Regions" />
                    <Tab icon={<FaUsers />} label="User Management" />
                </Tabs>
            </Box>

            {activeTab === 0 && (
                <Box sx={{ borderRadius: "8px", padding: "20px", marginBottom: "30px", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={isEditingTenantName ? 10 : 11}>
                            <TextField label="Tenant Name" variant="outlined" value={tenantName} onChange={(e) => setTenantName(e.target.value)} disabled={!isEditingTenantName} fullWidth />
                        </Grid>
                        {!isEditingTenantName ? (
                            <Grid item xs={1}>
                                <Button variant="outlined" onClick={() => setIsEditingTenantName(true)} fullWidth sx={{ height: "56px", borderColor: "#342ead", color: "#342ead" }}>
                                    <FaEdit />
                                </Button>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={1}>
                                    <Button variant="contained" onClick={handleSaveTenantName} fullWidth sx={{ height: "56px", backgroundColor: "#342ead" }}>
                                        <FaSave />
                                    </Button>
                                </Grid>
                                <Grid item xs={1}>
                                    <Button variant="outlined" onClick={handleCancelTenantName} fullWidth sx={{ height: "56px", borderColor: "#d33", color: "#d33" }}>
                                        <FaTimes />
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>
            )}

            {activeTab === 1 && (
                <Box sx={{ borderRadius: "8px", padding: "20px", marginBottom: "30px", boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)" }}>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={10}>
                            <Autocomplete
                                multiple
                                options={AWS_REGIONS}
                                getOptionLabel={(option) => `${option.name} (${option.code})`}
                                value={AWS_REGIONS.filter(region => scanRegions.includes(region.code))}
                                onChange={(event, newValue) => setScanRegions(newValue.map(region => region.code))}
                                loading={loadingRegions}
                                disabled={loadingRegions}
                                renderInput={(params) => <TextField {...params} label="Select Cloud Regions" placeholder="Choose regions to scan" />}
                                renderTags={(value, getTagProps) => value.map((option, index) => <Chip label={option.code} {...getTagProps({ index })} sx={{ backgroundColor: "#342ead", color: "#fff" }} />)}
                            />
                            <Typography variant="caption" sx={{ marginTop: "8px", display: "block", color: "#666" }}>
                                Selected: {scanRegions.length} region{scanRegions.length !== 1 ? 's' : ''}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Button variant="contained" onClick={handleSaveRegions} fullWidth disabled={savingRegions || loadingRegions} sx={{ height: "56px", backgroundColor: "#342ead", marginTop: "0px" }}>
                                {savingRegions ? "Saving..." : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {activeTab === 2 && <UserManagement isSidebarOpen={isSidebarOpen} />}
        </Box>
    );
};

export default TenantSettingsPage;
